import React, { useRef, useEffect, useState, useCallback } from 'react';
import { ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';
import {
  skColor,
  hitTestVertex,
  hitTestContour,
  hitTestSegmentInsert,
  hitTestProbePin,
  pointsNear,
  findSnapOnContourPolylines,
  getDrawSnapPolylines,
  SNAP_RADIUS_SCREEN_PX,
} from './fiSnowDigitizerGeometry';

export default function ContourViewport({
  mapImage,
  panelWidth,
  panelHeight,
  contours,
  draftPoints,
  draftClosed,
  draftSk,
  selectedId,
  toolMode,
  beginUndoableEdit,
  onAddDraftPoint,
  onFinishDraft,
  onSelectContour,
  onMoveVertex,
  onInsertVertex,
  onDeleteVertex,
  onRemoveLastDraftPoint,
  probePins = [],
  probeHover,
  probeScreen,
  onProbeMove,
  onProbePin,
  onRemoveProbePin,
}) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const mapImageRef = useRef(null);
  const snapInfoRef = useRef(null);

  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 40, y: 40 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [mousePixel, setMousePixel] = useState([0, 0]);
  const [snapInfo, setSnapInfo] = useState(null);
  const [dragged, setDragged] = useState(null);

  const snapRadiusPx = SNAP_RADIUS_SCREEN_PX / scale;

  const getSnapPoint = useCallback(
    (pixel) => {
      if (toolMode !== 'draw') return null;
      const polylines = getDrawSnapPolylines(contours, draftPoints);
      return findSnapOnContourPolylines(pixel, polylines, snapRadiusPx);
    },
    [toolMode, contours, draftPoints, snapRadiusPx],
  );

  const resolvePointerPixel = useCallback(
    (pixel, { forDraw = false } = {}) => {
      if (forDraw) {
        const snap = getSnapPoint(pixel);
        return snap?.point || pixel;
      }
      if (toolMode === 'select' && dragged) {
        const polylines = contours.map((c) => ({ points: c.points, closed: c.closed }));
        const snap = findSnapOnContourPolylines(pixel, polylines, snapRadiusPx);
        return snap?.point || pixel;
      }
      return pixel;
    },
    [toolMode, dragged, contours, getSnapPoint, snapRadiusPx],
  );

  const toScreen = useCallback(
    (px, py) => [px * scale + offset.x, py * scale + offset.y],
    [scale, offset],
  );

  const toPixel = useCallback(
    (sx, sy) => [(sx - offset.x) / scale, (sy - offset.y) / scale],
    [scale, offset],
  );

  const fitToView = useCallback(() => {
    const el = containerRef.current;
    if (!el || !panelWidth || !panelHeight) return;
    const pad = 24;
    const sx = (el.clientWidth - pad * 2) / panelWidth;
    const sy = (el.clientHeight - pad * 2) / panelHeight;
    const s = Math.min(sx, sy, 2.5);
    setScale(s);
    setOffset({
      x: (el.clientWidth - panelWidth * s) / 2,
      y: (el.clientHeight - panelHeight * s) / 2,
    });
  }, [panelWidth, panelHeight]);

  useEffect(() => {
    if (mapImage) {
      const img = new Image();
      img.onload = () => {
        mapImageRef.current = img;
        fitToView();
      };
      img.src = mapImage;
    }
  }, [mapImage, fitToView]);

  useEffect(() => {
    const onResize = () => fitToView();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [fitToView]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    const container = containerRef.current;
    if (!canvas || !ctx || !container) return;

    canvas.width = container.clientWidth;
    canvas.height = container.clientHeight;
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const img = mapImageRef.current;
    if (img) {
      ctx.drawImage(img, offset.x, offset.y, panelWidth * scale, panelHeight * scale);
    }

    const drawPolyline = (points, color, { closed, selected, width = 2 }) => {
      if (!points || points.length < 1) return;
      ctx.strokeStyle = color;
      ctx.lineWidth = (selected ? width + 1.5 : width) * Math.max(1, scale * 0.5);
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      if (selected) {
        ctx.shadowColor = color;
        ctx.shadowBlur = 6;
      }
      ctx.beginPath();
      const [x0, y0] = toScreen(points[0][0], points[0][1]);
      ctx.moveTo(x0, y0);
      for (let i = 1; i < points.length; i += 1) {
        const [x, y] = toScreen(points[i][0], points[i][1]);
        ctx.lineTo(x, y);
      }
      if (closed && points.length > 2) {
        ctx.closePath();
        ctx.fillStyle = `${color}22`;
        ctx.fill();
      }
      ctx.stroke();
      ctx.shadowBlur = 0;

      points.forEach((p, i) => {
        const [sx, sy] = toScreen(p[0], p[1]);
        ctx.beginPath();
        ctx.arc(sx, sy, (selected ? 5 : 4) * Math.max(0.8, scale * 0.35), 0, Math.PI * 2);
        ctx.fillStyle = selected ? '#fff' : color;
        ctx.fill();
        ctx.strokeStyle = '#020617';
        ctx.lineWidth = 1;
        ctx.stroke();
        if (i === 0 && closed) {
          ctx.strokeStyle = '#fbbf24';
          ctx.lineWidth = 2;
          ctx.stroke();
        }
      });
    };

    contours.forEach((c) => {
      drawPolyline(c.points, skColor(c.sk_kN_m2), {
        closed: c.closed,
        selected: c.id === selectedId,
      });
    });

    if (draftPoints.length > 0) {
      const previewCursor = toolMode === 'draw' && snapInfo ? snapInfo.point : mousePixel;
      const preview = [...draftPoints];
      if (toolMode === 'draw' && preview.length >= 1) {
        preview.push(previewCursor);
      }
      drawPolyline(preview, skColor(draftSk), {
        closed: false,
        selected: true,
        width: 2.5,
      });
      if (draftClosed && draftPoints.length >= 3) {
        const [fx, fy] = toScreen(draftPoints[0][0], draftPoints[0][1]);
        if (pointsNear(draftPoints[0], previewCursor, snapRadiusPx)) {
          ctx.beginPath();
          ctx.arc(fx, fy, 10, 0, Math.PI * 2);
          ctx.strokeStyle = '#fbbf24';
          ctx.lineWidth = 2;
          ctx.stroke();
        }
      }
    }

    if (snapInfo && toolMode === 'draw' && !dragged) {
      const [sptx, spty] = toScreen(snapInfo.point[0], snapInfo.point[1]);
      ctx.beginPath();
      ctx.arc(sptx, spty, 8, 0, Math.PI * 2);
      ctx.lineWidth = 2;
      if (snapInfo.type === 'corner') {
        ctx.strokeStyle = '#22c55e';
        ctx.stroke();
        ctx.strokeRect(sptx - 4, spty - 4, 8, 8);
      } else {
        ctx.strokeStyle = '#3b82f6';
        ctx.stroke();
      }
    }

    (probePins || []).forEach((pin, idx) => {
      const px = pin.pixel_x ?? pin.pixel?.[0];
      const py = pin.pixel_y ?? pin.pixel?.[1];
      if (px == null || py == null) return;
      const [sx, sy] = toScreen(px, py);
      const size = 7;
      ctx.strokeStyle = '#fbbf24';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(sx - size, sy);
      ctx.lineTo(sx + size, sy);
      ctx.moveTo(sx, sy - size);
      ctx.lineTo(sx, sy + size);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(sx, sy, 4, 0, Math.PI * 2);
      ctx.stroke();
      const label = `${pin.sk_kN_m2}`;
      ctx.font = '600 12px system-ui, sans-serif';
      ctx.fillStyle = 'rgba(15, 23, 42, 0.9)';
      const tw = ctx.measureText(label).width;
      ctx.fillRect(sx + 8, sy - 16, tw + 8, 18);
      ctx.fillStyle = '#fbbf24';
      ctx.fillText(label, sx + 12, sy - 3);
      ctx.fillStyle = '#94a3b8';
      ctx.font = '500 10px system-ui, sans-serif';
      ctx.fillText(`#${idx + 1}`, sx + 12, sy + 10);
    });

    if (toolMode === 'probe' && !isPanning) {
      const [mx, my] = toScreen(mousePixel[0], mousePixel[1]);
      ctx.strokeStyle = 'rgba(251, 191, 36, 0.85)';
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.moveTo(mx - 12, my);
      ctx.lineTo(mx + 12, my);
      ctx.moveTo(mx, my - 12);
      ctx.lineTo(mx, my + 12);
      ctx.stroke();
      ctx.setLineDash([]);
    }
  }, [
    contours,
    draftPoints,
    draftClosed,
    draftSk,
    selectedId,
    toolMode,
    mousePixel,
    snapInfo,
    dragged,
    offset,
    scale,
    panelWidth,
    panelHeight,
    toScreen,
    snapRadiusPx,
    probePins,
    isPanning,
  ]);

  useEffect(() => {
    draw();
  }, [draw]);

  const handleWheel = (e) => {
    e.preventDefault();
    const rect = canvasRef.current.getBoundingClientRect();
    const sx = e.clientX - rect.left;
    const sy = e.clientY - rect.top;
    const factor = e.deltaY < 0 ? 1.12 : 1 / 1.12;
    const newScale = Math.min(8, Math.max(0.15, scale * factor));
    const px = (sx - offset.x) / scale;
    const py = (sy - offset.y) / scale;
    setScale(newScale);
    setOffset({ x: sx - px * newScale, y: sy - py * newScale });
  };

  const handleMouseDown = (e) => {
    if (e.button === 1 || (e.button === 0 && e.shiftKey)) {
      setIsPanning(true);
      setPanStart({ x: e.clientX - offset.x, y: e.clientY - offset.y });
      return;
    }

    const rect = canvasRef.current.getBoundingClientRect();
    const rawPixel = toPixel(e.clientX - rect.left, e.clientY - rect.top);

    if (e.button === 2) {
      e.preventDefault();
      if (toolMode === 'probe') {
        const pinId = hitTestProbePin(rawPixel, probePins, scale);
        if (pinId) onRemoveProbePin?.(pinId);
        return;
      }
      if (toolMode === 'draw' && draftPoints.length > 0) {
        onRemoveLastDraftPoint?.();
        return;
      }
      if (toolMode === 'select') {
        const vtx = hitTestVertex(rawPixel, contours, scale);
        if (vtx) {
          beginUndoableEdit?.();
          onDeleteVertex?.(vtx.contourId, vtx.vertexIndex);
        }
      }
      return;
    }

    if (e.button !== 0) return;

    if (toolMode === 'probe') {
      onProbePin?.(rawPixel[0], rawPixel[1]);
      return;
    }

    const pixel = resolvePointerPixel(rawPixel, { forDraw: toolMode === 'draw' });

    if (toolMode === 'select') {
      const vtx = hitTestVertex(rawPixel, contours, scale);
      if (vtx) {
        beginUndoableEdit?.();
        setDragged(vtx);
        onSelectContour(vtx.contourId);
        return;
      }
      const insert = hitTestSegmentInsert(rawPixel, contours, scale, selectedId);
      if (insert) {
        beginUndoableEdit?.();
        onInsertVertex?.(insert.contourId, insert.insertIndex, insert.point);
        onSelectContour(insert.contourId);
        return;
      }
      const cid = hitTestContour(rawPixel, contours, scale);
      onSelectContour(cid);
      return;
    }

    if (toolMode === 'draw') {
      if (draftClosed && draftPoints.length >= 3 && pointsNear(draftPoints[0], pixel, snapRadiusPx)) {
        onFinishDraft?.();
        return;
      }
      beginUndoableEdit?.();
      onAddDraftPoint(pixel);
    }
  };

  const handleMouseMove = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const rawPixel = toPixel(e.clientX - rect.left, e.clientY - rect.top);
    setMousePixel(rawPixel);

    if (toolMode === 'probe') {
      onProbeMove?.(rawPixel[0], rawPixel[1], { x: e.clientX - rect.left, y: e.clientY - rect.top });
      setSnapInfo(null);
      snapInfoRef.current = null;
    } else if (toolMode === 'draw') {
      const snap = getSnapPoint(rawPixel);
      setSnapInfo(snap);
      snapInfoRef.current = snap;
    } else {
      setSnapInfo(null);
      snapInfoRef.current = null;
    }

    if (isPanning) {
      setOffset({ x: e.clientX - panStart.x, y: e.clientY - panStart.y });
      return;
    }

    if (dragged && onMoveVertex) {
      const pixel = resolvePointerPixel(rawPixel);
      onMoveVertex(dragged.contourId, dragged.vertexIndex, pixel);
    }
  };

  const handleMouseUp = () => {
    setIsPanning(false);
    setDragged(null);
  };

  const handleDblClick = (e) => {
    if (toolMode === 'draw' && draftPoints.length >= 2) {
      e.preventDefault();
      onFinishDraft?.();
    }
  };

  return (
    <div className="fsd-viewport-wrap" ref={containerRef}>
      <div className="fsd-toolbar">
        <button type="button" className="fsd-btn" onClick={() => setScale((s) => Math.min(8, s * 1.2))} title="Zoom in">
          <ZoomIn size={16} />
        </button>
        <button type="button" className="fsd-btn" onClick={() => setScale((s) => Math.max(0.15, s / 1.2))} title="Zoom out">
          <ZoomOut size={16} />
        </button>
        <button type="button" className="fsd-btn" onClick={fitToView} title="Fit map">
          <Maximize2 size={16} />
        </button>
      </div>
      <canvas
        ref={canvasRef}
        className={`fsd-canvas${isPanning ? ' panning' : ''}${toolMode === 'probe' ? ' probe' : ''}`}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onDoubleClick={handleDblClick}
        onContextMenu={(e) => e.preventDefault()}
      />
      {toolMode === 'probe' && probeHover && probeScreen && (
        <div
          className={`fsd-probe-readout${probeHover.loading || probeHover.error ? ' muted' : ''}`}
          style={{ left: probeScreen.x + 14, top: probeScreen.y + 14 }}
        >
          {probeHover.loading && 'Sampling…'}
          {!probeHover.loading && probeHover.error && probeHover.error}
          {!probeHover.loading && !probeHover.error && probeHover.sk_kN_m2 != null && (
            <>s_k = {probeHover.sk_kN_m2} kN/m²</>
          )}
        </div>
      )}
      <div className="fsd-hint">
        {toolMode === 'probe'
          ? 'Probe: hover for s_k · click to pin · right-click pin to remove · Shift+drag pan'
          : 'Draw: snapped clicks (green corner / blue edge) · right-click removes last draft point · Select: click segment to insert node · right-click node to delete · Shift+drag pan · Scroll zoom · Ctrl+Z undo'}
      </div>
    </div>
  );
}
